import { ComponentPublicInstance } from 'vue';

import { isObject } from './object';

export type LazySelectable = string | Element;

/**
 * Ensure that `el` is returned as an Element.
 */
export function lazySelector(el: LazySelectable) {
  let selectedElement: unknown;

  if (typeof el === 'string') {
    selectedElement = document.querySelector(el);
  } else {
    selectedElement = el;
  }

  assertIsHTMLElement(selectedElement);

  return selectedElement;
}

export function isElement(obj: unknown): obj is Element {
  return isObject(obj) && 'nodeType' in obj && obj.nodeType === Node.ELEMENT_NODE;
}

export function isVueComponent(element: unknown): element is ComponentPublicInstance {
  return element !== null && typeof element === 'object' && '$el' in element;
}

export function isHTMLElement(obj: unknown): obj is HTMLElement {
  return isElement(obj);
}

export function assertIsHTMLElement(obj: unknown): asserts obj is HTMLElement {
  if (!isHTMLElement(obj)) {
    console.log(obj);
    throw new Error(`Expected HTMLElement, got ${typeof obj}.`);
  }
}

const focusableSelectors = [
  'a[href]',
  'area[href]',
  'input:not([type="hidden"]):not([type="radio"]):not([disabled])',
  'input[type="radio"]:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'button:not([disabled])',
  'iframe',
  'audio[controls]',
  'video[controls]',
  '[contenteditable]',
  '[tabindex]',
  '[data-focusable]',
];

/**
 * Return an array of all focusable elements that are within the element passed as an argument.
 */
export function getFocusableElements(el: Element) {
  const focusableEls = el.querySelectorAll(focusableSelectors.join(','));
  return Array.from(focusableEls).filter(isHTMLElement);
}

/**
 * Return an array of all currently focusable elements (excluding `tabindex="-1") that are within the element passed as an argument.
 */
export function getCurrentlyFocusableElements(el: Element) {
  const focusableEls = el.querySelectorAll(
    focusableSelectors.map((selector) => `${selector}:not([tabindex^="-"])`).join(','),
  );

  return Array.from(focusableEls).filter(isHTMLElement);
}

/**
 * Return true when the element is focusable (excluding `tabindex="-1"`).
 */
export function isFocusableElement(el: Element) {
  if (!isHTMLElement(el)) {
    return false;
  }

  const focusableSelector = focusableSelectors.map((selector) => `${selector}:not([tabindex^="-"])`).join(',');

  return el.matches(focusableSelector);
}

/**
 * Disable focusable children inside the element passed as argument.
 */
export function disableFocusableChildren(el: Element | null) {
  if (!el) {
    return;
  }

  getFocusableElements(el).forEach((focusable) => {
    focusable.tabIndex = -1;
  });
}

/**
 * Enable focusable children inside the element passed as argument.
 */
export function enableFocusableChildren(el: Element | null) {
  if (!el) {
    return;
  }

  getFocusableElements(el).forEach((focusable) => {
    focusable.tabIndex = 0;
  });
}

/**
 * Save the currently active element
 * @return A function to restore the previously active element
 */
export function saveActiveElement() {
  const activeElement = document.activeElement as HTMLElement | null;

  return () => {
    activeElement?.focus();

    return activeElement ?? null;
  };
}

export function handleClickAway(el: string | Element, cb: () => void) {
  const element = lazySelector(el);

  function onClickAway(e: Event): void {
    if (element && !element.contains(e.target as Node)) {
      cb();
    }
  }

  document.addEventListener('click', onClickAway, { once: true });
}
